import uniqueId from 'lodash-es/uniqueId';
import { getColorLuminance } from '~/utils/color';
import { getVuukleLogo } from '~/utils/general';

/**
 * AD units classes blueprint
 * @abstract
 */
abstract class ADUnit {
  /** Wrapper that contains AD div and AD script code inside that is ready to be inserted into the DOm */
  public abstract wrapper: HTMLDivElement;
  /** Generated AD ID that we will assign to the DIV element and pass to ADs library, so it can find our div and load AD there  */
  public advertisementID: string;

  // Unique ad identifier
  public uniqueID: string;

  constructor(
    protected hideLabelLogo = false,
    generateUniqueId = true
  ) {
    if (generateUniqueId) {
      this.uniqueID = uniqueId();
    }
    this.advertisementID = `div-gpt-ad-1497448474263${this.uniqueID ? `-${this.uniqueID}` : ''}`;
  }

  protected createWrapperElement(hideLabel: boolean = false): [HTMLDivElement, HTMLDivElement] {
    /** AD unit wrapper that will contain script and AD div where we want to inser AD unit */
    const adWrapper = document.createElement('div');
    /** AD unit will be here */
    const adDiv = document.createElement('div');
    /** Script code that will contain code to init AD inside adWrapper */
    const adScript = document.createElement('script');

    adWrapper.className = 'vuukle-ads';
    // We need some styles to make div responsive and prevent overflowing
    adWrapper.setAttribute(
      'style',
      `display: block !important;height: auto;margin: 0px auto;text-align: center; clear: both; overflow: hidden;`
    );

    adDiv.id = this.advertisementID;

    adScript.type = 'text/javascript';
    adScript.innerHTML = ``; // Supposed to be changed inside inherited class

    adDiv.appendChild(adScript);
    adWrapper.appendChild(adDiv);

    /** If the label is not being intentionally hidden in config, show it after 3 seconds */
    if (!hideLabel) {
      setTimeout(() => {
        const adLabel = this.createAdLabel(adWrapper);
        adWrapper.insertAdjacentElement('afterbegin', adLabel);
        this.watchAdDiv(adWrapper, adDiv);
      }, 3000);
    }

    return [adWrapper, adDiv];
  }

  /**
   * Add advertisement label
   * @param adWrapper
   */
  protected createAdLabel(adWrapper: HTMLElement) {
    const luminance = getColorLuminance(adWrapper);

    const adLabelContainer = document.createElement('div');
    adLabelContainer.className = 'vuukle-ad-label';
    adLabelContainer.style.cssText = `
      display: flex;
      justify-content: space-evenly;
      flex-basis: 100%;
      margin: 0 auto 5px;
      width: 100px;
      height: 11px;
      line-height: 1.1 !important;
      padding: 0px;
    `;
    const adLabelLogoContainer = document.createElement('span');
    adLabelLogoContainer.style.display = 'none';
    // if (this.hideLabelLogo) {
    //   adLabelLogoContainer.style.display = 'none';
    // } else {
    //   adLabelLogoContainer.style.cssText = `
    //     display: block;
    //     margin: 0px;
    //     line-height: 0 !important;
    //     padding: 0px;
    //   `;
    // }

    const adLabelLogo = document.createElement('a');
    adLabelLogo.ariaLabel = 'Vuukle';
    adLabelLogo.href = 'https://vuukle.com';
    adLabelLogo.target = '_blank';
    adLabelLogo.rel = 'noopener nofollow';
    adLabelLogo.style.cssText = `
      background-color: transparent; 
      box-shadow: none;
    `;
    adLabelLogo.innerHTML = getVuukleLogo(11, '0 0 30 30');

    adLabelLogoContainer.appendChild(adLabelLogo);

    const adLabelText = document.createElement('span');
    adLabelText.textContent = 'Advertisement';
    adLabelText.style.cssText = `
      display: block;
      font-size: 10px !important;
      color: ${luminance <= 0.3 ? '#fff' : '#6c757d'};
      margin: 0px;
      text-align: center;
      flex: 1;
      padding: 0px;
      line-height: 1 !important;
    `;

    adLabelContainer.appendChild(adLabelLogoContainer);
    adLabelContainer.appendChild(adLabelText);

    return adLabelContainer;
  }

  /**
   * Watch the ad div for changes and resize the label accordingly
   * @param adWrapper
   * @param adDiv
   */
  protected watchAdDiv(adWrapper: HTMLElement, adDiv: HTMLElement) {
    /**
     * Ad label resize callback
     * ResizeObserver can cause performance issues related to the INP Core Web Vitals metric
     * Which is why we're using a setTimeout here
     * @see https://web.dev/articles/manually-diagnose-slow-interactions-in-the-lab#how_to_identify_presentation_delays
     */
    function callback() {
      setTimeout(() => {
        const label: HTMLDivElement | null = adWrapper.querySelector('.vuukle-ad-label');
        const adIframe: HTMLIFrameElement | null =
          adDiv.firstElementChild && adDiv.firstElementChild.querySelector('iframe');
        // if there's an ad, display label and adjust label width to match the ad width
        if (label && adIframe) {
          const adWidth = parseInt(adIframe.width) < 80 ? 80 : parseInt(adIframe.width);
          label.style.width = adWidth + 'px';
          adIframe.style.setProperty('width', `${adWidth}px`, 'important');
        } else if (label) {
          label.style.width = '80px'; // Ensure the label width will always be at least 80 in all cases
        }
      }, 0);
    }

    /** Use ResizeObserver with a fallback to Mutation Observer */
    if (typeof window.ResizeObserver !== 'undefined') {
      const resizeObserver = new window.ResizeObserver(callback);
      resizeObserver.observe(adDiv);
    }
    /** Use MutationObserver to resize the ad label element every time the ad container updates */
    const config = { childList: true, subtree: true };
    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);
    // Start observing the target node for configured mutations
    observer.observe(adDiv, config);
  }
}

export default ADUnit;
